import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import './ProductShowcase.css';

const trackerVariants = {
  M: '44207220326445', // Medium variant ID
  L: '44207220359213'  // Large variant ID
};

export default function ProductShowcase() {
  const [selectedSize, setSelectedSize] = useState(null); // Default null to require explicit size selection
  const [sizeError, setSizeError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [promoCode, setPromoCode] = useState('');
  const [isDiscountApplied, setIsDiscountApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [bannerPromo, setBannerPromo] = useState(null);
  const [isAffirmModalOpen, setIsAffirmModalOpen] = useState(false);

  useEffect(() => {
    window.applyClubCodeFromSelector = (code) => {
      setPromoCode(code);
      setIsDiscountApplied(true);
      setPromoError('');
    };

    return () => {
      delete window.applyClubCodeFromSelector;
    };
  }, []);

  // Listen for live promo banner sale settings from Firestore
  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = onSnapshot(
        doc(db, 'siteSettings', 'promo'), 
        (docSnap) => {
          if (docSnap.exists() && docSnap.data().isActive) {
            setBannerPromo(docSnap.data());
          } else {
            setBannerPromo(null);
          }
        },
        (err) => {
          console.warn('ProductShowcase promo listener warning:', err);
        }
      );
    } catch (e) {
      console.warn('ProductShowcase promo catch:', e);
    }
    return () => unsub();
  }, []);

  const productImages = [
    { src: '/playermaker_box_transparent.webp', alt: 'Playermaker 2.0 Canada smart soccer tracker boxed kit' },
    { src: '/playermaker_case_sensors.webp', alt: 'Playermaker charging case with smart tracking sensor pods' },
    { src: '/playermaker_strap.webp', alt: 'Playermaker replacement strap with smart sensor tracker attached' },
    { src: '/playermaker_case.webp', alt: 'Closed Playermaker smart charging case with logo' },
    { src: '/playermaker_strap_sensor.webp', alt: 'Silicon cleat strap with built-in Playermaker tracking sensor' },
  ];

  const basePrice = 279;

  // Extract banner sale discount if active (e.g. "$50 OFF" -> 50, "$43 OFF" -> 43)
  let bannerDiscount = 0;
  if (bannerPromo && bannerPromo.isActive) {
    const match = (bannerPromo.highlight || '').match(/(\d+)/);
    if (match) {
      bannerDiscount = parseInt(match[1], 10);
    }
  }

  // Club code discount amount
  const clubDiscount = isDiscountApplied ? 30 : 0;

  // Combined discount
  const totalDiscountAmount = bannerDiscount + clubDiscount;
  const hasDiscount = totalDiscountAmount > 0;
  const finalPrice = Math.max(0, basePrice - totalDiscountAmount);

  const handleApplyPromo = (e) => {
    e.preventDefault();
    const VALID_CLUB_CODES = [
      'FVSL2026',
      'HAL2026',
      'ICSF2026',
      'PSA2026',
      'PMSC2026',
      'TSA2026',
      'TSSFC2026',
      'VOLF2026',
      'WVFC2026'
    ];
    const code = promoCode.trim().toUpperCase();
    if (VALID_CLUB_CODES.includes(code)) {
      setIsDiscountApplied(true);
      setPromoError('');
    } else {
      setPromoError('Invalid club code. Try "FVSL2026" for testing.');
      setIsDiscountApplied(false);
    }
  };

  const handleBuyClick = (e) => {
    if (!selectedSize) {
      e.preventDefault();
      setSizeError(true);
      const sizeEl = document.getElementById('size-selector-group');
      if (sizeEl) {
        sizeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  return (
    <section className="product-section" id="product-showcase">
      <div className="container">
        <div className="product-grid">
          {/* Product Image Column with Gallery */}
          <div className="product-image-container">
            <div className="image-wrapper main-display" style={{ position: 'relative' }}>
              <img 
                src={productImages[activeImageIndex].src} 
                alt={productImages[activeImageIndex].alt} 
                className="product-image" 
                width="1000"
                height="856"
                style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
              />
              <span className="image-badge">REAL DEVICE</span>
              {/* Pulsing blue LED on the case image */}
              {activeImageIndex === 1 && (
                <div className="blue-led-pulse" style={{ position: 'absolute', top: '15%', left: '72.9%' }} />
              )}
            </div>
            
            <div className="gallery-thumbnails">
              {productImages.map((image, idx) => (
                <button
                  key={idx}
                  className={`thumb-btn ${idx === activeImageIndex ? 'active' : ''}`}
                  onClick={() => setActiveImageIndex(idx)}
                >
                  <img 
                    src={image.src} 
                    alt={`Playermaker Canada Detail view, ${image.alt}`} 
                    className="thumb-img" 
                    width="150"
                    height="150"
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details Column */}
          <div className="product-details">
            <span className="badge-volt">OFFICIAL CANADIAN DISTRIBUTOR</span>
            <h2 className="product-title">Playermaker 2.0 Smart Tracker</h2>
            
            {/* Rating */}
            <div className="product-rating">
              <span className="stars">★★★★★</span>
              <span className="rating-text">4.9/5 (1,240+ Canadian Players)</span>
            </div>

            {/* Google Store / Merchant Ratings Widget Container */}
            <div className="google-merchant-widget-box" id="google-merchant-widget-container" style={{ marginTop: '1.2rem', marginBottom: '1.8rem' }}>
              <div className="google-widget-badge">
                <svg className="google-g-icon" viewBox="0 0 24 24" width="22" height="22">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                </svg>
                <div className="google-widget-content">
                  <div className="google-widget-title-row">
                    <span className="google-title">Google Store Rating</span>
                    <span className="google-verified-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Verified Merchant
                    </span>
                  </div>
                  <div className="google-widget-details-row">
                    <span className="g-stars">★★★★★</span>
                    <span className="g-rating-score">4.9 / 5.0</span>
                    <span className="g-reviews-count">• Google Store Reviews</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Header */}
            <div className="product-price">
              {hasDiscount ? (
                <div className="price-display-wrapper">
                  <div className="price-row" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', flexWrap: 'wrap' }}>
                    <span className="price-original">${(basePrice * quantity).toFixed(2)}</span>
                    <span className="price-current">${(finalPrice * quantity).toFixed(2)} CAD</span>
                    <span className="sale-badge-red">${totalDiscountAmount * quantity} OFF</span>
                  </div>
                  <p className="price-savings-note" style={{ color: 'var(--accent-volt)', fontSize: '1.3rem', marginTop: '0.6rem' }}>
                    {bannerDiscount > 0 && isDiscountApplied
                      ? `✓ Sale Offer ($${bannerDiscount}) + Club Code ($30) Applied, You save $${totalDiscountAmount * quantity}.00!`
                      : bannerDiscount > 0
                      ? `✓ Active Banner Sale, Save $${bannerDiscount * quantity}.00!`
                      : `✓ Club Code Applied, Save $${clubDiscount * quantity}.00!`}
                  </p>
                </div>
              ) : (
                <span className="price-current">${(basePrice * quantity).toFixed(2)} CAD</span>
              )}
            </div>

            {/* Affirm / Shop Pay Installments Box */}
            <div className="affirm-installment-box">
              <div className="affirm-radio-row">
                <span className="affirm-radio-dot">●</span>
                <div className="affirm-text-wrap">
                  <span className="affirm-main-title">Pay in installments</span>
                  <span className="affirm-sub-title">Starting at <strong>${((finalPrice * quantity) / 4).toFixed(2)}</strong> / 2 weeks with no interest</span>
                </div>
              </div>
              <div className="affirm-footer-row">
                <span className="affirm-eligibility-text">
                  Subject to eligibility. Installments in partnership with <span className="affirm-brand">affirm</span>.
                </span>
                <button 
                  type="button" 
                  onClick={() => setIsAffirmModalOpen(true)} 
                  className="affirm-link-btn"
                >
                  View sample plans
                </button>
              </div>
            </div>

            <p className="product-description">
              The ultimate soccer performance package. Straps directly onto your boots to capture technical metrics that GPS vests miss, including foot contact speed, ball release angles, kick velocity, and detailed left vs right foot comparisons.
            </p>

            {/* Size Selector */}
            <div className="selector-group" id="size-selector-group">
              <span className="selector-label" style={{ color: sizeError ? '#FF003B' : 'inherit' }}>
                SELECT STRAP SIZE {sizeError && ',  PLEASE SELECT A SIZE'}
              </span>
              <div className="size-buttons">
                <button 
                  className={`size-btn ${selectedSize === 'M' ? 'active' : ''} ${sizeError ? 'size-btn-error' : ''}`}
                  onClick={() => { setSelectedSize('M'); setSizeError(false); }}
                >
                  <span className="size-title">Medium (M)</span>
                  <span className="size-desc">Cleat sizes US 5.5, US 8</span>
                </button>
                <button 
                  className={`size-btn ${selectedSize === 'L' ? 'active' : ''} ${sizeError ? 'size-btn-error' : ''}`}
                  onClick={() => { setSelectedSize('L'); setSizeError(false); }}
                >
                  <span className="size-title">Large (L)</span>
                  <span className="size-desc">Cleat sizes US 8.5+</span>
                </button>
              </div>
            </div>

            {/* Club Discount Field */}
            <div className="selector-group">
              <span className="selector-label">ACADEMY CLUB CODE</span>
              <form onSubmit={handleApplyPromo} className="promo-form">
                <input 
                  type="text" 
                  placeholder="Enter club code for extra $30 off" 
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="promo-input"
                  disabled={isDiscountApplied}
                />
                <button 
                  type="submit" 
                  className={`btn promo-btn ${isDiscountApplied ? 'btn-applied' : 'btn-outline'}`}
                  disabled={isDiscountApplied}
                >
                  {isDiscountApplied ? 'Applied ✓' : 'Apply'}
                </button>
              </form>
              {isDiscountApplied && (
                <p className="promo-status success">✓ Club code successfully applied. Enjoy an extra $30 off!</p>
              )}
              {promoError && <p className="promo-status error">{promoError}</p>}
              {!isDiscountApplied && (
                <p className="promo-hint">
                  Tip: Need a code? Enter your{' '}
                  <a 
                    href="#club-partners" 
                    onClick={(e) => { 
                      e.preventDefault(); 
                      const el = document.getElementById('club-partners'); 
                      if (el) el.scrollIntoView({ behavior: 'smooth' }); 
                    }} 
                    style={{ cursor: 'pointer', color: 'var(--accent-volt)', textDecoration: 'underline', fontWeight: 800 }}
                  >
                    TEAMCODE
                  </a>{' '}
                  or select your club below.
                </p>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="selector-group quantity-selector-group">
              <span className="selector-label">QUANTITY</span>
              <div className="quantity-controls">
                <button 
                  type="button" 
                  className="quantity-btn decrement" 
                  onClick={() => setQuantity(prev => Math.max(1, prev, 1))}
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className="quantity-display">{quantity}</span>
                <button 
                  type="button" 
                  className="quantity-btn increment" 
                  onClick={() => setQuantity(prev => prev + 1)}
                >
                  +
                </button>
              </div>
            </div>

            {/* Total Breakdown Bar */}
            <div className="total-summary-card">
              <span className="total-title">Total:</span>
              <div className="total-values">
                <div className="total-price-row">
                  {hasDiscount && (
                    <span className="price-original">${(basePrice * quantity).toFixed(2)}</span>
                  )}
                  <span className="total-amount">${(finalPrice * quantity).toFixed(2)} CAD</span>
                </div>
                {hasDiscount && (
                  <span className="sale-badge-red">${totalDiscountAmount * quantity} OFF</span>
                )}
              </div>
            </div>

            {/* Size selection error reminder popup */}
            {sizeError && (
              <div className="size-reminder-box">
                <span className="size-reminder-icon">⚠️</span>
                <span>Please select a strap size (Medium or Large) to continue.</span>
              </div>
            )}

            {/* Direct Add to Cart / Checkout Redirect Button */}
            <button
              type="button"
              onClick={() => {
                if (!selectedSize) {
                  setSizeError(true);
                  const sizeEl = document.getElementById('size-selector-group');
                  if (sizeEl) sizeEl.scrollIntoView({ behavior: 'smooth' });
                  return;
                }
                const cleanCode = promoCode.trim().toUpperCase();
                const discountCode = (cleanCode === 'PSA2026') ? 'PSA2025' : (cleanCode || 'FVSL2026');
                const variantId = trackerVariants[selectedSize];
                const SHOPIFY_DOMAIN = 'https://kimeoconsulting.myshopify.com';
                let checkoutUrl = `${SHOPIFY_DOMAIN}/cart/${variantId}:${quantity}`;
                if (hasDiscount) {
                  checkoutUrl = `${SHOPIFY_DOMAIN}/discount/${discountCode}?redirect=%2Fcart%2F${variantId}%3A${quantity}`;
                }
                window.location.href = checkoutUrl;
              }}
              className="btn btn-volt checkout-direct-btn pulse-glow-volt"
              style={{ width: '100%', textAlign: 'center', display: 'block', marginBottom: '1.6rem' }}
            >
              {selectedSize 
                ? `BUY PLAYERMAKER 2.0: $${(finalPrice * quantity).toFixed(2)} CAD \u2192` 
                : 'SELECT SIZE & BUY NOW \u2192'}
            </button>

            {/* Trust Badges */}
            <div className="product-trust-badges">
              <div className="trust-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-volt)" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>Lifetime Warranty</span>
              </div>
              <div className="trust-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-volt)" strokeWidth="2">
                  <rect x="1" y="3" width="15" height="13" />
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                <span>Free Expedited Shipping</span>
              </div>
              <div className="trust-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-volt)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>No Monthly Subscriptions</span>
              </div>
              <div className="trust-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-volt)" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
                <span>Annual Subscription Included</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Affirm / Shop Pay Sample Plans Modal Portal */}
      {isAffirmModalOpen && createPortal(
        <div className="affirm-modal-overlay" onClick={(e) => { if (e.target.className === 'affirm-modal-overlay') setIsAffirmModalOpen(false); }}>
          <div className="affirm-modal-card animate-pop-in">
            <div className="affirm-modal-header">
              <div className="shop-pay-badge">
                <span className="shop-pay-logo">shop <span className="shop-pay-bold">Pay</span></span>
                <span className="shop-pay-title">Get it now, pay later</span>
              </div>
              <button type="button" className="affirm-modal-close" onClick={() => setIsAffirmModalOpen(false)} aria-label="Close modal">×</button>
            </div>

            <div className="affirm-modal-body">
              <h3 className="sample-plans-title">Sample plans for <strong>${(finalPrice * quantity).toFixed(2)}</strong> purchase</h3>

              <div className="plans-container">
                {/* 4 interest-free biweekly payments */}
                <div className="plan-card active">
                  <div className="plan-header-row">
                    <span className="plan-amount"><strong>${((finalPrice * quantity) / 4).toFixed(2)}</strong> every 2 weeks for 8 weeks</span>
                  </div>
                  <div className="plan-detail-row">
                    <span>Interest (0% APR)</span>
                    <span>$0.00</span>
                  </div>
                  <div className="plan-detail-row total-row">
                    <span>Total</span>
                    <span><strong>${(finalPrice * quantity).toFixed(2)}</strong></span>
                  </div>
                </div>

                {/* 12 monthly payments with APR */}
                <div className="plan-card">
                  <div className="plan-header-row">
                    <span className="plan-amount"><strong>${(((finalPrice * quantity) * 1.083) / 12).toFixed(2)}</strong> every month for 12 months</span>
                  </div>
                  <div className="plan-detail-row">
                    <span>Interest (15% APR)</span>
                    <span>${((finalPrice * quantity) * 0.083).toFixed(2)}</span>
                  </div>
                  <div className="plan-detail-row total-row">
                    <span>Total</span>
                    <span><strong>${((finalPrice * quantity) * 1.083).toFixed(2)}</strong></span>
                  </div>
                </div>
              </div>

              <p className="affirm-disclaimer-text">
                The estimated payment amount excludes taxes and shipping. Rates range from 0-32% APR (subject to provincial regulatory limits). Payment options through Shop Pay Installments are subject to an eligibility check and approval. Options depend on your purchase amount, and a down payment may be required. More options may be available upon approval. Available in select provinces and/or territories. Visit <a href="https://affirm.ca/help" target="_blank" rel="noopener noreferrer">affirm.ca/help</a> for more info.
              </p>

              <div className="affirm-modal-footer">
                <span className="shop-pay-brand">shop <span className="shop-pay-bold">Pay</span></span>
                <span className="affirm-partnership-text">Installments in partnership with <span className="affirm-brand">affirm</span></span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
